// frontend/src/components/SignalMonitor.tsx
import { apiUrl } from '../config/paths';
// Версия: 2.4.0 (2026-01-18) - Добавлен фильтр периода дежурства
//
// ИЗМЕНЕНИЯ:
// - v2.4.0: Добавлен фильтр периода (С / По) с datetime-local inputs
// - v2.4.0: Добавлена кнопка "Текущее дежурство" (05:00 UTC → 05:00 UTC)
// - v2.4.0: Фильтрация по датам применяется к полю received_at
// - v2.2.0: Двойной клик на координаты → карта
// - Импортирован модуль statusLabels
// - Заменены hardcoded статусы на локализованные
// - getStatusColor теперь использует getSignalStatusColor из statusLabels
// - formatCoordinates поддерживает coordinates: { lat, lon } и { latitude, longitude }
// ИСПРАВЛЕНО 2026-01-13: добавлены закрывающие скобки для apiUrl()

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  CircularProgress,
  TablePagination,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import EventIcon from '@mui/icons-material/Event';
// Иконки для карточки заявки
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import ScheduleIcon from '@mui/icons-material/Schedule';

// Импорт единой локализации
import {
  SIGNAL_STATUS_LABELS,
  getSignalStatusLabel,
  getSignalStatusColor,
  getSignalTypeLabel,
  getSignalTypeColor,
  isRealAlert,
  getRequestStatusLabel,
  getRequestStatusColor,
} from '../utils/statusLabels';

// ═══════════════════════════════════════════════════════════════════
// УТИЛИТА: Вычисление периода дежурства (05:00 UTC → 05:00 UTC)
// ═══════════════════════════════════════════════════════════════════
const getShiftPeriod = (): { start: string; end: string } => {
  const now = new Date();
  const today5Utc = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 5, 0, 0)
  );

  const tomorrow5Utc = new Date(today5Utc);
  tomorrow5Utc.setUTCDate(tomorrow5Utc.getUTCDate() + 1);

  let shiftStart: Date;
  let shiftEnd: Date;

  if (now < today5Utc) {
    shiftStart = new Date(today5Utc);
    shiftStart.setUTCDate(shiftStart.getUTCDate() - 1);
    shiftEnd = today5Utc;
  } else {
    shiftStart = today5Utc;
    shiftEnd = tomorrow5Utc;
  }

  // Формат для datetime-local: YYYY-MM-DDTHH:MM
  const formatForInput = (date: Date): string => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return {
    start: formatForInput(shiftStart),
    end: formatForInput(shiftEnd),
  };
};

function parseUtcDateTimeLocal(value?: string): Date | null {
  if (!value) return null;
  const normalized = value.endsWith('Z') ? value : `${value}:00Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

interface Signal {
  id: number;
  mmsi: string;
  terminal_number?: string;
  vessel_name?: string;
  imo_number?: string;
  call_sign?: string;
  received_at: string;
  created_at?: string;
  updated_at?: string;
  detection_time?: string;
  position_updated_at?: string;
  positionUpdatedAt?: string;
  status: string;
  latitude?: number;
  longitude?: number;
  coordinates?: {
    lat?: number;
    lon?: number;
    lng?: number;
    latitude?: number;
    longitude?: number;
    raw?: string;
  };
  request_id?: string;
  request_status?: string;
  request?: {
    status?: string;
  };
  metadata?: Record<string, unknown>;
  suggestions?: unknown;
  sender_email?: string;
  subject?: string;
  email_subject?: string;
  raw_message?: string;
  rawMessage?: string;
  signal_type?: string;
  original_alert_type?: string;
  alert_resolution?: AlertResolution | null;
  alert_resolution_comment?: string | null;
  alert_resolved_at?: string | null;
  alert_resolved_by?: string | null;
}

type AlertResolution = 'REAL_ALERT' | 'FALSE_ALARM';

interface SignalStats {
  matched?: number;
  unmatched?: number;
  total: number;
  allTime?: {
    total: number;
    matched: number;
    confirmed: number;
    unmatched: number;
  };
  byStatus: {
    MATCHED?: number;
    UNMATCHED?: number;
    ERROR?: number;
  };
  byType: {
    SSAS_TEST?: number;
    EPIRB?: number;
    VHF_DSC?: number;
  };
}

// ═══════════════════════════════════════════════════════════════════
// Интерфейс заявки (для карточки заявки в модальном окне)
// ═══════════════════════════════════════════════════════════════════
interface Request {
  id: number;
  request_number?: string;
  vessel_name?: string;
  mmsi?: string;
  imo_number?: string;
  ssas_number?: string;
  terminal_number?: string;
  owner_organization?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  test_date?: string;
  planned_test_date?: string;
  start_time?: string;
  end_time?: string;
  status?: string;
  signal_id?: number | string | null;
  created_at?: string;
  updated_at?: string;
  notes?: string;
}

// HELPER: getAuthHeaders
const getAuthHeaders = (): HeadersInit => {
  const token =
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('auth_token') ||
    sessionStorage.getItem('token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const HIDE_ALERTS_STORAGE_KEY = 'ssto.operator.hideAlerts';
const ALERT_SIREN_STORAGE_KEY = 'ssto.operator.alertSirenEnabled';

function getStoredBoolean(key: string, defaultValue: boolean): boolean {
  try {
    const value = localStorage.getItem(key);
    if (value === null) return defaultValue;
    return value === 'true';
  } catch {
    return defaultValue;
  }
}

function setStoredBoolean(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // localStorage may be unavailable in private mode; keep UI state in memory.
  }
}

function formatUtcDate(dateStr?: string): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return (
    date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    }) + ' UTC'
  );
}

const DISPLAY_FUTURE_TOLERANCE_MS = 48 * 60 * 60 * 1000;

function parseDisplayDate(value?: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isReasonableDisplayDate(value?: string, anchorValue?: string): boolean {
  const date = parseDisplayDate(value);
  if (!date) return false;

  const now = new Date();
  const year = date.getUTCFullYear();
  if (year < 2000 || year > now.getUTCFullYear() + 1) return false;

  const anchor = parseDisplayDate(anchorValue);
  const reference = anchor || now;
  return date.getTime() <= reference.getTime() + DISPLAY_FUTURE_TOLERANCE_MS;
}

function getSignalReceivedDate(signal: Signal): {
  value?: string;
  needsReview: boolean;
  systemReference?: string;
} {
  const anchor = signal.created_at || signal.updated_at;
  if (signal.received_at && isReasonableDisplayDate(signal.received_at, anchor)) {
    return { value: signal.received_at, needsReview: false };
  }

  return {
    value: signal.received_at,
    needsReview: Boolean(signal.received_at),
    systemReference: [signal.created_at, signal.updated_at].find((value) =>
      isReasonableDisplayDate(value)
    ),
  };
}

function metadataString(signal: Signal, ...keys: string[]): string {
  const metadata = signal.metadata || {};
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
}

function metadataValue(signal: Signal, ...keys: string[]): unknown {
  const metadata = signal.metadata || {};
  for (const key of keys) {
    const value = metadata[key];
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && !value.trim()) continue;
    return value;
  }
  return undefined;
}

function formatSignalValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.trim() || '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const values = value.map((item) => formatSignalValue(item)).filter((item) => item !== '—');
    return values.length > 0 ? values.join(', ') : '—';
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatMaybeUtcDate(value: unknown): string {
  return typeof value === 'string' && value.trim() ? formatUtcDate(value) : '—';
}

function getSignalSubject(signal: Signal): string {
  return formatSignalValue(
    signal.subject ||
      signal.email_subject ||
      metadataValue(signal, 'subject', 'email_subject', 'emailSubject')
  );
}

function getSignalRawMessage(signal: Signal): string {
  const rawMessage =
    signal.raw_message || signal.rawMessage || metadataValue(signal, 'raw_message', 'rawMessage');
  return typeof rawMessage === 'string' ? rawMessage.trim() : '';
}

function getSignalRequestStatus(signal: Signal): string {
  return formatSignalValue(signal.request_status || signal.request?.status);
}

function getCoordinateUpdateTime(signal: Signal): { value?: string; needsReview: boolean } {
  const candidate =
    signal.position_updated_at ||
    signal.positionUpdatedAt ||
    metadataString(
      signal,
      'position_updated_at',
      'positionUpdatedAt',
      'position_updated',
      'positionUpdated',
      'coordinates_updated_at',
      'coordinatesUpdatedAt',
      'coordinate_updated_at',
      'coordinateUpdatedAt',
      'position_updated_raw',
      'positionUpdatedRaw'
    ) ||
    '';

  if (!candidate) return { needsReview: false };

  const anchor = signal.received_at || signal.created_at || signal.updated_at;
  return {
    value: candidate,
    needsReview: !isReasonableDisplayDate(candidate, anchor),
  };
}

function isCombatAlertSignal(signal: Signal): boolean {
  const signalTypes = [signal.signal_type, signal.original_alert_type]
    .map((value) =>
      String(value || '')
        .trim()
        .toUpperCase()
    )
    .filter(Boolean);

  return signalTypes.some(
    (signalType) =>
      signalType === 'SSAS_ALERT' ||
      signalType === 'ALERT' ||
      signalType === 'REAL_ALERT' ||
      signalType === 'DISTRESS' ||
      signalType.includes('ТРЕВОГ') ||
      signalType.includes('БОЕВ')
  );
}

function isTestSignal(signal: Signal): boolean {
  const signalTypes = [signal.signal_type, signal.original_alert_type]
    .map((value) =>
      String(value || '')
        .trim()
        .toUpperCase()
    )
    .filter(Boolean);

  return signalTypes.some(
    (signalType) =>
      signalType === 'SSAS_TEST' || signalType === 'TEST' || signalType.includes('ТЕСТ')
  );
}

function isUnprocessedCombatAlert(signal: Signal): boolean {
  return isCombatAlertSignal(signal) && !signal.alert_resolution;
}

function canOpenManualLink(signal: Signal): boolean {
  const status = String(signal.status || '').toUpperCase();
  const terminal = String(signal.terminal_number || '').trim();
  return (
    status === 'UNMATCHED' &&
    !signal.request_id &&
    terminal.length > 0 &&
    isTestSignal(signal) &&
    !isCombatAlertSignal(signal)
  );
}

function isExcludedSimilarAlertStatus(statusValue: unknown): boolean {
  const status = String(statusValue || '')
    .trim()
    .toUpperCase();
  return ['DUPLICATE', 'DISMISSED', 'REJECTED', 'CANCELLED', 'CANCELED'].includes(status);
}

function getSimilarAlertSignals(source: Signal, candidates: Signal[]): Signal[] {
  const terminal = String(source.terminal_number || '').trim();
  const sourceReceivedAt = new Date(source.received_at);
  if (!terminal || Number.isNaN(sourceReceivedAt.getTime())) return [];

  const fromMs = sourceReceivedAt.getTime() - 60 * 60 * 1000;
  const toMs = sourceReceivedAt.getTime() + 60 * 60 * 1000;

  return candidates.filter((candidate) => {
    if (candidate.id === source.id) return false;
    const candidateTerminal = String(candidate.terminal_number || '').trim();
    if (candidateTerminal !== terminal) return false;
    if (!isCombatAlertSignal(candidate) || isTestSignal(candidate)) return false;
    if (candidate.alert_resolution) return false;
    if (isExcludedSimilarAlertStatus(candidate.status)) return false;
    const receivedAt = new Date(candidate.received_at);
    if (Number.isNaN(receivedAt.getTime())) return false;
    return receivedAt.getTime() >= fromMs && receivedAt.getTime() <= toMs;
  });
}

function formatUtcTime(date: Date): string {
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function formatSimilarAlertWindow(source: Signal): string {
  const sourceReceivedAt = new Date(source.received_at);
  if (Number.isNaN(sourceReceivedAt.getTime())) return '';

  const from = new Date(sourceReceivedAt.getTime() - 60 * 60 * 1000);
  const to = new Date(sourceReceivedAt.getTime() + 60 * 60 * 1000);
  return `${formatUtcTime(from)} по ${formatUtcTime(to)} UTC`;
}

function formatAlertResolution(value: unknown): string {
  const normalized = String(value || '')
    .trim()
    .toUpperCase();

  if (!normalized) return '—';
  if (['REAL_ALERT', 'ALERT', 'SSAS_ALERT'].includes(normalized)) return 'Боевой сигнал';
  if (['FALSE_ALARM', 'FALSE'].includes(normalized)) return 'Ложный сигнал';
  return formatSignalValue(value);
}

function getAlertResolutionLabel(signal: Signal): string {
  if (!isCombatAlertSignal(signal)) return getSignalStatusLabel(signal.status);
  if (signal.alert_resolution === 'REAL_ALERT') return 'Боевой сигнал';
  if (signal.alert_resolution === 'FALSE_ALARM') return 'Ложный сигнал';
  return 'ТРЕВОГА ССТО';
}

function getAlertResolutionColor(
  signal: Signal
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  if (!isCombatAlertSignal(signal)) return getSignalStatusColor(signal.status);
  if (signal.alert_resolution === 'REAL_ALERT') return 'error';
  if (signal.alert_resolution === 'FALSE_ALARM') return 'warning';
  return 'error';
}

const SignalMonitor: React.FC = () => {
  const navigate = useNavigate();
  const [signals, setSignals] = useState<Signal[]>([]);
  const [stats, setStats] = useState<SignalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [page, setPage] = useState(0);
  const [totalFromApi, setTotalFromApi] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [hideAlerts, setHideAlerts] = useState(() =>
    getStoredBoolean(HIDE_ALERTS_STORAGE_KEY, false)
  );
  const [alertSirenEnabled, setAlertSirenEnabled] = useState(() =>
    getStoredBoolean(ALERT_SIREN_STORAGE_KEY, true)
  );
  const audioContextRef = useRef<AudioContext | null>(null);
  const sirenOscillatorRef = useRef<OscillatorNode | null>(null);
  const sirenGainRef = useRef<GainNode | null>(null);
  const sirenLfoRef = useRef<OscillatorNode | null>(null);
  const sirenLfoGainRef = useRef<GainNode | null>(null);

  // ═══════════════════════════════════════════════════════════════════
  // STATE: Модальное окно «Карточка заявки»
  // ═══════════════════════════════════════════════════════════════════
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [selectedSignalDetails, setSelectedSignalDetails] = useState<Signal | null>(null);

  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedAlertSignal, setSelectedAlertSignal] = useState<Signal | null>(null);
  const [alertResolution, setAlertResolution] = useState<AlertResolution>('REAL_ALERT');
  const [alertResolutionComment, setAlertResolutionComment] = useState('');
  const [alertResolutionSaving, setAlertResolutionSaving] = useState(false);
  const [alertResolutionError, setAlertResolutionError] = useState<string | null>(null);
  const [alertResolutionSuccess, setAlertResolutionSuccess] = useState<string | null>(null);
  const [applyForSimilarAlerts, setApplyForSimilarAlerts] = useState(false);
  const [manualLinkingSignalId, setManualLinkingSignalId] = useState<number | null>(null);
  const [manualLinkDialogOpen, setManualLinkDialogOpen] = useState(false);
  const [manualLinkSignal, setManualLinkSignal] = useState<Signal | null>(null);
  const [manualLinkCandidates, setManualLinkCandidates] = useState<Request[]>([]);
  const [manualLinkCandidatesLoading, setManualLinkCandidatesLoading] = useState(false);
  const [manualLinkDialogError, setManualLinkDialogError] = useState<string | null>(null);
  const [manualLinkSuccess, setManualLinkSuccess] = useState<string | null>(null);

  // ═══════════════════════════════════════════════════════════════════
  // STATE: Фильтр по периоду дежурства
  // ═══════════════════════════════════════════════════════════════════
  const initialShift = getShiftPeriod();
  const [dateFrom, setDateFrom] = useState<string>(initialShift.start);
  const [dateTo, setDateTo] = useState<string>(initialShift.end);

  const similarAlertSignals = useMemo(
    () => (selectedAlertSignal ? getSimilarAlertSignals(selectedAlertSignal, signals) : []),
    [selectedAlertSignal, signals]
  );
  const similarAlertIds = useMemo(
    () => similarAlertSignals.map((signal) => signal.id),
    [similarAlertSignals]
  );
  const similarAlertWindowLabel = selectedAlertSignal
    ? formatSimilarAlertWindow(selectedAlertSignal)
    : '';

  const startSiren = () => {
    if (sirenOscillatorRef.current) return;

    try {
      const AudioCtor =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtor) return;

      const context =
        !audioContextRef.current || audioContextRef.current.state === 'closed'
          ? new AudioCtor()
          : audioContextRef.current;
      audioContextRef.current = context;
      if (context.state === 'suspended') {
        void context.resume().catch(() => undefined);
      }

      const start = context.currentTime;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const lfo = context.createOscillator();
      const lfoGain = context.createGain();

      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(850, start);

      lfo.type = 'sine';
      lfo.frequency.setValueAtTime(0.8, start);
      lfoGain.gain.setValueAtTime(180, start);
      lfo.connect(lfoGain);
      lfoGain.connect(oscillator.frequency);

      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(0.1, start + 0.08);

      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(start);
      lfo.start(start);

      sirenOscillatorRef.current = oscillator;
      sirenGainRef.current = gain;
      sirenLfoRef.current = lfo;
      sirenLfoGainRef.current = lfoGain;
    } catch {
      // Browser audio may be blocked before user interaction; signal table must remain usable.
    }
  };

  const stopSiren = (closeContext = false) => {
    const context = audioContextRef.current;
    const gain = sirenGainRef.current;
    const oscillator = sirenOscillatorRef.current;
    const lfo = sirenLfoRef.current;

    try {
      if (context && gain) {
        const now = context.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setTargetAtTime(0.0001, now, 0.03);
      }
      oscillator?.stop();
      lfo?.stop();
    } catch {
      // Nodes may already be stopped or unavailable.
    }

    try {
      oscillator?.disconnect();
      lfo?.disconnect();
      sirenLfoGainRef.current?.disconnect();
      gain?.disconnect();
    } catch {
      // Ignore disconnect races while unmounting.
    }

    sirenOscillatorRef.current = null;
    sirenGainRef.current = null;
    sirenLfoRef.current = null;
    sirenLfoGainRef.current = null;

    if (closeContext && context && context.state !== 'closed') {
      void context.close().catch(() => undefined);
      audioContextRef.current = null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // HANDLER: Клик по чипу заявки → открыть карточку заявки в модальном окне
  // ═══════════════════════════════════════════════════════════════════
  const handleRequestChipClick = async (requestId: string) => {
    setRequestLoading(true);
    setRequestDialogOpen(true);
    setSelectedRequest(null);
    try {
      const response = await fetch(apiUrl(`requests/${requestId}`), {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Ошибка загрузки заявки');
      const data = await response.json();
      // API может вернуть { data: request } или request напрямую
      const request = data.data || data;
      setSelectedRequest(request);
    } catch (err) {
      console.error('Ошибка загрузки заявки:', err);
      setRequestDialogOpen(false);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleCloseRequestDialog = () => {
    setRequestDialogOpen(false);
    setSelectedRequest(null);
  };

  const handleOpenSignalDetails = (signal: Signal) => {
    setSelectedSignalDetails(signal);
  };

  const handleCloseSignalDetails = () => {
    setSelectedSignalDetails(null);
  };

  const handleOpenAlertDialog = (signal: Signal) => {
    setSelectedAlertSignal(signal);
    setAlertResolution('REAL_ALERT');
    setAlertResolutionComment('');
    setApplyForSimilarAlerts(false);
    setAlertResolutionError(null);
    setAlertDialogOpen(true);
  };

  const handleOpenAlertDialogFromDetails = (signal: Signal) => {
    handleCloseSignalDetails();
    handleOpenAlertDialog(signal);
  };

  const handleCloseAlertDialog = () => {
    if (alertResolutionSaving) return;
    setAlertDialogOpen(false);
    setSelectedAlertSignal(null);
    setAlertResolutionError(null);
    setApplyForSimilarAlerts(false);
  };

  const handleResolveAlert = async () => {
    if (!selectedAlertSignal) return;

    setAlertResolutionSaving(true);
    setAlertResolutionError(null);
    setAlertResolutionSuccess(null);

    const comment = alertResolutionComment.trim();
    try {
      const response = await fetch(apiUrl(`signals/${selectedAlertSignal.id}/resolve-alert`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          resolution: alertResolution,
          ...(comment ? { comment } : {}),
          applyForSimilar: applyForSimilarAlerts ? similarAlertIds : [],
        }),
      });

      const responseBody = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          responseBody?.message ||
          responseBody?.error ||
          'Не удалось сохранить результат обработки сигнала';
        throw new Error(Array.isArray(message) ? message.join(', ') : String(message));
      }

      const updatedSignal = responseBody?.data || responseBody;
      if (!applyForSimilarAlerts && updatedSignal && typeof updatedSignal === 'object') {
        setSignals((currentSignals) =>
          currentSignals.map((signal) =>
            signal.id === selectedAlertSignal.id
              ? {
                  ...signal,
                  ...updatedSignal,
                }
              : signal
          )
        );
      } else {
        await fetchSignals();
      }

      setAlertDialogOpen(false);
      setSelectedAlertSignal(null);
      setAlertResolutionComment('');
      setApplyForSimilarAlerts(false);
      setAlertResolutionSuccess(
        applyForSimilarAlerts && similarAlertIds.length > 0
          ? `Результат обработки применён к ${similarAlertIds.length + 1} сигналам`
          : 'Результат обработки сигнала сохранён'
      );
      fetchStats();
    } catch (err) {
      setAlertResolutionError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    } finally {
      setAlertResolutionSaving(false);
    }
  };

  const filterManualLinkCandidates = (requests: Request[], signal: Signal): Request[] => {
    const terminal = String(signal.terminal_number || '').trim();
    return requests.filter((request) => {
      const requestTerminal = String(request.ssas_number || request.terminal_number || '').trim();
      const requestStatus = String(request.status || '').toLowerCase();
      return requestTerminal === terminal && requestStatus === 'pending' && !request.signal_id;
    });
  };

  const loadManualLinkCandidates = async (signal: Signal) => {
    const terminal = String(signal.terminal_number || '').trim();
    if (!terminal) {
      setManualLinkCandidates([]);
      setManualLinkDialogError('У сигнала не указан terminal_number');
      return;
    }

    setManualLinkCandidatesLoading(true);
    setManualLinkDialogError(null);

    try {
      const response = await fetch(
        apiUrl(`requests/pending?terminal=${encodeURIComponent(terminal)}`),
        {
          headers: getAuthHeaders(),
          credentials: 'include',
        }
      );
      const responseBody = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          responseBody?.message ||
          responseBody?.error ||
          'Не удалось загрузить заявки для ручной привязки';
        throw new Error(Array.isArray(message) ? message.join(', ') : String(message));
      }

      const requests: Request[] = Array.isArray(responseBody)
        ? responseBody
        : responseBody?.data || responseBody?.items || [];
      setManualLinkCandidates(filterManualLinkCandidates(requests, signal));
    } catch (err) {
      setManualLinkCandidates([]);
      setManualLinkDialogError(
        err instanceof Error ? err.message : 'Неизвестная ошибка загрузки заявок'
      );
    } finally {
      setManualLinkCandidatesLoading(false);
    }
  };

  const handleManualLinkSignal = async (signal: Signal) => {
    setManualLinkSignal(signal);
    setManualLinkDialogOpen(true);
    setManualLinkCandidates([]);
    setManualLinkSuccess(null);
    await loadManualLinkCandidates(signal);
  };

  const handleManualLinkSignalFromDetails = (signal: Signal) => {
    handleCloseSignalDetails();
    void handleManualLinkSignal(signal);
  };

  const handleCloseManualLinkDialog = () => {
    if (manualLinkingSignalId) return;
    setManualLinkDialogOpen(false);
    setManualLinkSignal(null);
    setManualLinkCandidates([]);
    setManualLinkDialogError(null);
  };

  const handleConfirmManualLink = async (request: Request) => {
    if (!manualLinkSignal) return;

    setManualLinkingSignalId(manualLinkSignal.id);
    setError(null);
    setManualLinkDialogError(null);
    setManualLinkSuccess(null);

    try {
      const response = await fetch(apiUrl(`signals/${manualLinkSignal.id}/link`), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ requestId: request.id, override: false }),
      });

      const responseBody = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          responseBody?.message || responseBody?.error || 'Не удалось вручную связать сигнал';
        throw new Error(Array.isArray(message) ? message.join(', ') : String(message));
      }

      setManualLinkSuccess(
        `Сигнал #${manualLinkSignal.id} вручную связан с заявкой #${request.id}`
      );
      setManualLinkDialogOpen(false);
      setManualLinkSignal(null);
      setManualLinkCandidates([]);
      await fetchSignals();
      fetchStats();
    } catch (err) {
      setManualLinkDialogError(
        err instanceof Error ? err.message : 'Неизвестная ошибка ручной привязки'
      );
    } finally {
      setManualLinkingSignalId(null);
    }
  };

  const fetchSignals = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
      });
      const search = filter.trim();
      if (search) params.set('search', search);
      const dateFromUtc = parseUtcDateTimeLocal(dateFrom);
      const dateToUtc = parseUtcDateTimeLocal(dateTo);
      if (dateFromUtc) params.set('startDate', dateFromUtc.toISOString());
      if (dateToUtc) params.set('endDate', dateToUtc.toISOString());

      const response = await fetch(apiUrl(`signals?${params.toString()}`));
      if (!response.ok) throw new Error('Ошибка загрузки сигналов');
      const data = await response.json();
      const items = Array.isArray(data) ? data : data.items || [];
      const total = data.total ?? items.length;
      setSignals(items);
      setTotalFromApi(total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // ИСПРАВЛЕНО: добавлена закрывающая скобка )
      const response = await fetch(apiUrl('signals/stats'));
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Ошибка загрузки статистики:', err);
    }
  };

  useEffect(() => {
    fetchSignals();
    fetchStats();
  }, [page, pageSize, filter, dateFrom, dateTo]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchSignals();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, page, pageSize, filter, dateFrom, dateTo]);

  useEffect(() => {
    const armAudio = () => {
      try {
        const AudioCtor =
          window.AudioContext ||
          (window as typeof window & { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!AudioCtor) return;
        const context = audioContextRef.current || new AudioCtor();
        audioContextRef.current = context;
        if (context.state === 'suspended') {
          void context.resume().catch(() => undefined);
        }
      } catch {
        // Ignore unavailable audio devices/blocked contexts.
      }
    };

    window.addEventListener('pointerdown', armAudio, { once: true });
    window.addEventListener('keydown', armAudio, { once: true });
    return () => {
      window.removeEventListener('pointerdown', armAudio);
      window.removeEventListener('keydown', armAudio);
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // Текстовый поиск и период применяются на backend до пагинации.
  // ═══════════════════════════════════════════════════════════════════
  const filteredSignalsBeforeAlertFilter = useMemo(() => {
    return signals;
  }, [signals]);

  const hiddenAlertCount = useMemo(
    () => filteredSignalsBeforeAlertFilter.filter((s) => isRealAlert(s.signal_type || '')).length,
    [filteredSignalsBeforeAlertFilter]
  );

  const filteredSignals = useMemo(() => {
    if (!hideAlerts) return filteredSignalsBeforeAlertFilter;
    return filteredSignalsBeforeAlertFilter.filter((s) => !isRealAlert(s.signal_type || ''));
  }, [filteredSignalsBeforeAlertFilter, hideAlerts]);

  // Siren follows the visible/filtered list so hidden alerts do not alarm.
  const visibleSignalsForSiren = filteredSignals;
  const activeSirenAlertSignals = useMemo(
    () => visibleSignalsForSiren.filter((signal) => isUnprocessedCombatAlert(signal)),
    [visibleSignalsForSiren]
  );

  useEffect(() => {
    if (alertSirenEnabled && activeSirenAlertSignals.length > 0) {
      startSiren();
    } else {
      stopSiren();
    }
  }, [activeSirenAlertSignals.length, alertSirenEnabled]);

  useEffect(() => {
    return () => stopSiren(true);
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // ОБРАБОТЧИК: Установить текущее дежурство
  // ═══════════════════════════════════════════════════════════════════
  const handleSetCurrentShift = () => {
    const shift = getShiftPeriod();
    setDateFrom(shift.start);
    setDateTo(shift.end);
  };

  // ═══════════════════════════════════════════════════════════════════
  // ОБРАБОТЧИК: Сбросить фильтр периода
  // ═══════════════════════════════════════════════════════════════════
  const handleClearPeriod = () => {
    setDateFrom('');
    setDateTo('');
  };

  // Поддержка обоих форматов: плоские поля и вложенный coordinates (lat/lon и latitude/longitude)
  const formatCoordinates = (signal: Signal): string => {
    const lat = signal.latitude ?? signal.coordinates?.lat ?? signal.coordinates?.latitude;
    const lon =
      signal.longitude ??
      signal.coordinates?.lng ??
      signal.coordinates?.lon ??
      signal.coordinates?.longitude;
    if (lat === undefined || lon === undefined) return '—';
    return `${lat.toFixed(4)}°, ${lon.toFixed(4)}°`;
  };

  const formatRequestInterval = (request: Request): string => {
    if (request.start_time && request.end_time) {
      return `${formatUtcDate(request.start_time)} → ${formatUtcDate(request.end_time)}`;
    }
    const planned = request.planned_test_date || request.test_date;
    return planned ? formatUtcDate(planned) : '—';
  };

  // Получение координат для навигации на карту
  const getCoords = (signal: Signal): { lat: number; lon: number } | null => {
    const lat = signal.latitude ?? signal.coordinates?.lat ?? signal.coordinates?.latitude;
    const lon =
      signal.longitude ??
      signal.coordinates?.lng ??
      signal.coordinates?.lon ??
      signal.coordinates?.longitude;
    if (lat === undefined || lon === undefined) return null;
    return { lat, lon };
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SignalCellularAltIcon /> Мониторинг сигналов
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {alertResolutionSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAlertResolutionSuccess(null)}>
          {alertResolutionSuccess}
        </Alert>
      )}

      {manualLinkSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setManualLinkSuccess(null)}>
          {manualLinkSuccess}
        </Alert>
      )}

      {/* Статистика */}
      {stats && (
        <Grid data-print="hide" container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Всего сигналов
                </Typography>
                <Typography variant="h4">{stats.allTime?.total || stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #4caf50' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {SIGNAL_STATUS_LABELS.matched}
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.allTime?.matched || stats.matched || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #ff9800' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {SIGNAL_STATUS_LABELS.unmatched}
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.allTime?.unmatched || stats.unmatched || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #f44336' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {SIGNAL_STATUS_LABELS.error}
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.byStatus?.ERROR || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ФИЛЬТРЫ: Текстовый поиск + Период + Кнопки управления */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Box
        data-print="hide"
        sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}
      >
        {/* Текстовый поиск */}
        <TextField
          size="small"
          placeholder="Поиск по терминалу, судну, статусу..."
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 300 }}
        />

        {/* Кнопка Обновить */}
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchSignals();
            fetchStats();
          }}
          disabled={loading}
        >
          {loading ? 'Загрузка...' : 'Обновить'}
        </Button>

        {/* Кнопка Авто-обновление */}
        <Button
          variant={autoRefresh ? 'contained' : 'outlined'}
          onClick={() => setAutoRefresh(!autoRefresh)}
          size="small"
        >
          Авто-обновление: {autoRefresh ? 'ВКЛ' : 'ВЫКЛ'}
        </Button>

        <FormControlLabel
          control={
            <Switch
              checked={hideAlerts}
              onChange={(event) => {
                const next = event.target.checked;
                setHideAlerts(next);
                setStoredBoolean(HIDE_ALERTS_STORAGE_KEY, next);
                setPage(0);
              }}
              size="small"
            />
          }
          label="Скрывать ТРЕВОГА ССТО"
        />

        <FormControlLabel
          control={
            <Switch
              checked={alertSirenEnabled}
              onChange={(event) => {
                const next = event.target.checked;
                setAlertSirenEnabled(next);
                setStoredBoolean(ALERT_SIREN_STORAGE_KEY, next);
              }}
              size="small"
            />
          }
          label="Сирена"
        />

        {hideAlerts && hiddenAlertCount > 0 && (
          <Chip label={`Скрыто тревог: ${hiddenAlertCount}`} size="small" color="warning" />
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* КНОПКА: Текущее дежурство (после Авто-обновления) */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<EventIcon />}
          onClick={handleSetCurrentShift}
          size="small"
          title="Установить период текущего дежурства (05:00 UTC → 05:00 UTC)"
        >
          Текущее дежурство
        </Button>

        {/* Кнопка Печать */}
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          size="small"
        >
          Печать
        </Button>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ФИЛЬТР ПЕРИОДА: С / По */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Box
        data-print="hide"
        sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          label="С"
          type="datetime-local"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          label="По"
          type="datetime-local"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        />

        {/* Кнопка сброса периода */}
        {(dateFrom || dateTo) && (
          <Button
            variant="text"
            size="small"
            onClick={handleClearPeriod}
            sx={{ color: 'text.secondary' }}
          >
            Сбросить период
          </Button>
        )}
      </Box>

      {/* Только для печати: счётчик записей */}
      <Box
        sx={{
          display: 'none',
          '@media print': { display: 'block', mt: 1, mb: 1, fontStyle: 'italic', fontSize: '10pt' },
        }}
      >
        Показано {filteredSignals.length} из {totalFromApi || signals.length} сигналов
      </Box>

      {/* Таблица сигналов */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>ID</TableCell>
              <TableCell>Терминал</TableCell>
              <TableCell>Судно</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell sx={{ width: 220, maxWidth: 220 }}>Статус</TableCell>
              <TableCell>Координаты</TableCell>
              <TableCell>Получен</TableCell>
              <TableCell>Заявка</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSignals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {loading ? 'Загрузка...' : 'Нет сигналов'}
                </TableCell>
              </TableRow>
            ) : (
              filteredSignals.map((signal) => (
                <TableRow
                  key={signal.id}
                  hover
                  onClick={() => handleOpenSignalDetails(signal)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isRealAlert(signal.signal_type || '')
                      ? 'rgba(211, 47, 47, 0.1)'
                      : 'inherit',
                  }}
                >
                  <TableCell>{signal.id}</TableCell>
                  <TableCell>
                    <code style={{ fontWeight: 'bold' }}>{signal.terminal_number || '—'}</code>
                  </TableCell>
                  <TableCell>{signal.vessel_name || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getSignalTypeLabel(signal.signal_type || 'UNKNOWN')}
                      size="small"
                      color={getSignalTypeColor(signal.signal_type || 'UNKNOWN')}
                      variant={isRealAlert(signal.signal_type || '') ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 220, maxWidth: 220 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 0.5,
                      }}
                    >
                      <Chip
                        label={getAlertResolutionLabel(signal)}
                        size="small"
                        color={getAlertResolutionColor(signal)}
                      />
                      {isCombatAlertSignal(signal) && signal.alert_resolution_comment && (
                        <Typography variant="caption" color="text.secondary">
                          Комментарий: {signal.alert_resolution_comment}
                        </Typography>
                      )}
                      {isCombatAlertSignal(signal) && signal.alert_resolved_at && (
                        <Typography variant="caption" color="text.secondary">
                          Обработано: {formatUtcDate(signal.alert_resolved_at)}
                        </Typography>
                      )}
                      {isCombatAlertSignal(signal) && signal.alert_resolved_by && (
                        <Typography variant="caption" color="text.secondary">
                          ОД: {signal.alert_resolved_by}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {/* КООРДИНАТЫ - двойной клик переходит на карту */}
                  <TableCell
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e3f2fd' } }}
                    onClick={(event) => event.stopPropagation()}
                    onDoubleClick={(event) => {
                      event.stopPropagation();
                      const coords = getCoords(signal);
                      if (coords) {
                        navigate(
                          `/operator/map?lat=${coords.lat}&lon=${coords.lon}&status=${signal.status}&id=${signal.id}`
                        );
                      }
                    }}
                    title="Двойной клик — показать на карте"
                  >
                    {(() => {
                      const coordinateUpdateTime = getCoordinateUpdateTime(signal);
                      return (
                        <Box sx={{ lineHeight: 1.35 }}>
                          <Typography variant="body2">{formatCoordinates(signal)}</Typography>
                          {coordinateUpdateTime.value && (
                            <Typography
                              variant="caption"
                              color={
                                coordinateUpdateTime.needsReview ? 'warning.main' : 'text.secondary'
                              }
                              display="block"
                            >
                              обновл. координат: {formatUtcDate(coordinateUpdateTime.value)}
                            </Typography>
                          )}
                          {coordinateUpdateTime.value && coordinateUpdateTime.needsReview && (
                            <Typography variant="caption" color="warning.main" display="block">
                              дата из сообщения требует проверки
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const received = getSignalReceivedDate(signal);
                      return (
                        <Box sx={{ lineHeight: 1.35 }}>
                          <Typography variant="body2">{formatUtcDate(received.value)}</Typography>
                          {received.needsReview && (
                            <Typography variant="caption" color="warning.main" display="block">
                              received_at требует служебной проверки
                            </Typography>
                          )}
                          {received.needsReview && received.systemReference && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              системная дата: {formatUtcDate(received.systemReference)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {signal.request_id ? (
                      <Chip
                        label={`#${signal.request_id}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRequestChipClick(signal.request_id!);
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    {isUnprocessedCombatAlert(signal) || canOpenManualLink(signal) ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {isUnprocessedCombatAlert(signal) && (
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenAlertDialog(signal);
                            }}
                          >
                            Обработать
                          </Button>
                        )}
                        {canOpenManualLink(signal) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleManualLinkSignal(signal);
                            }}
                            disabled={manualLinkingSignalId === signal.id}
                          >
                            {manualLinkingSignalId === signal.id ? 'Связь...' : 'Связать'}
                          </Button>
                        )}
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalFromApi}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[50, 100, 200]}
        labelRowsPerPage="На странице:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
      />

      {selectedSignalDetails &&
        (() => {
          const signal = selectedSignalDetails;
          const coordinateUpdateTime = getCoordinateUpdateTime(signal);
          const positionUpdatedRaw = formatSignalValue(
            metadataValue(signal, 'position_updated_raw', 'positionUpdatedRaw')
          );
          const course = formatSignalValue(
            metadataValue(signal, 'course', 'course_over_ground', 'courseOverGround', 'cog')
          );
          const speed = formatSignalValue(
            metadataValue(signal, 'speed', 'speed_over_ground', 'speedOverGround', 'sog')
          );
          const senderEmail = formatSignalValue(
            signal.sender_email || metadataValue(signal, 'sender_email', 'senderEmail', 'sender')
          );
          const rawMessage = getSignalRawMessage(signal);
          const showAlertSection =
            isCombatAlertSignal(signal) ||
            Boolean(
              signal.alert_resolution ||
              signal.alert_resolution_comment ||
              signal.alert_resolved_at ||
              signal.alert_resolved_by
            );

          return (
            <Dialog
              open={Boolean(selectedSignalDetails)}
              onClose={handleCloseSignalDetails}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Подробная информация о сигнале #{signal.id}
                <IconButton
                  aria-label="close"
                  onClick={handleCloseSignalDetails}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>
                      Основное
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          ID
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          #{signal.id}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Терминал
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                          {signal.terminal_number || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Судно
                        </Typography>
                        <Typography variant="body1">{signal.vessel_name || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          MMSI
                        </Typography>
                        <Typography variant="body1">{signal.mmsi || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          IMO
                        </Typography>
                        <Typography variant="body1">{signal.imo_number || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Позывной
                        </Typography>
                        <Typography variant="body1">{signal.call_sign || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Тип
                        </Typography>
                        <Chip
                          label={getSignalTypeLabel(signal.signal_type || 'UNKNOWN')}
                          size="small"
                          color={getSignalTypeColor(signal.signal_type || 'UNKNOWN')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Исходный тип
                        </Typography>
                        <Typography variant="body1">{signal.original_alert_type || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Статус
                        </Typography>
                        <Chip
                          label={getAlertResolutionLabel(signal)}
                          size="small"
                          color={getAlertResolutionColor(signal)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Получен
                        </Typography>
                        <Typography variant="body1">
                          {formatMaybeUtcDate(getSignalReceivedDate(signal).value)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Создан
                        </Typography>
                        <Typography variant="body1">
                          {formatMaybeUtcDate(signal.created_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Обновлён
                        </Typography>
                        <Typography variant="body1">
                          {formatMaybeUtcDate(signal.updated_at)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>
                      Координаты
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Координаты
                        </Typography>
                        <Typography variant="body1">{formatCoordinates(signal)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Обновление координат
                        </Typography>
                        <Typography variant="body1">
                          {formatMaybeUtcDate(coordinateUpdateTime.value)}
                        </Typography>
                        {coordinateUpdateTime.value && coordinateUpdateTime.needsReview && (
                          <Typography variant="caption" color="warning.main">
                            дата из сообщения требует проверки
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Строка обновления координат
                        </Typography>
                        <Typography variant="body1">{positionUpdatedRaw}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Курс
                        </Typography>
                        <Typography variant="body1">{course}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Скорость
                        </Typography>
                        <Typography variant="body1">{speed}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>
                      Связь с заявкой
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Заявка
                        </Typography>
                        <Typography variant="body1">
                          {signal.request_id ? `#${signal.request_id}` : '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Статус заявки
                        </Typography>
                        <Typography variant="body1">{getSignalRequestStatus(signal)}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>
                      Email / сообщение
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Отправитель
                        </Typography>
                        <Typography variant="body1">{senderEmail}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Тема
                        </Typography>
                        <Typography variant="body1">{getSignalSubject(signal)}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        {rawMessage ? (
                          <Box component="details" sx={{ mt: 1 }}>
                            <Box
                              component="summary"
                              sx={{ cursor: 'pointer', fontWeight: 600, mb: 1 }}
                            >
                              Исходное сообщение
                            </Box>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: 13,
                              }}
                            >
                              {rawMessage}
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Исходное сообщение: —
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>

                  {showAlertSection && (
                    <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                      <Typography variant="h6" gutterBottom>
                        Обработка тревоги
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Решение по тревоге
                          </Typography>
                          <Typography variant="body1">
                            {formatAlertResolution(signal.alert_resolution)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Обработано
                          </Typography>
                          <Typography variant="body1">
                            {formatMaybeUtcDate(signal.alert_resolved_at || undefined)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Обработал
                          </Typography>
                          <Typography variant="body1">{signal.alert_resolved_by || '—'}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Комментарий
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {signal.alert_resolution_comment || '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </Box>
              </DialogContent>

              <DialogActions>
                {canOpenManualLink(signal) && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleManualLinkSignalFromDetails(signal)}
                    disabled={manualLinkingSignalId === signal.id}
                  >
                    {manualLinkingSignalId === signal.id ? 'Связь...' : 'Связать'}
                  </Button>
                )}
                {isUnprocessedCombatAlert(signal) && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleOpenAlertDialogFromDetails(signal)}
                  >
                    Обработать
                  </Button>
                )}
                <Button onClick={handleCloseSignalDetails}>Закрыть</Button>
              </DialogActions>
            </Dialog>
          );
        })()}

      <Dialog open={alertDialogOpen} onClose={handleCloseAlertDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          Обработка боевого сигнала
          <IconButton
            aria-label="close"
            onClick={handleCloseAlertDialog}
            disabled={alertResolutionSaving}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAlertSignal && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Сигнал #{selectedAlertSignal.id}
                </Typography>
                <Typography variant="body1">
                  Терминал: {selectedAlertSignal.terminal_number || '—'}
                </Typography>
              </Box>

              {alertResolutionError && <Alert severity="error">{alertResolutionError}</Alert>}

              <FormControl component="fieldset">
                <FormLabel component="legend">Результат обработки</FormLabel>
                <RadioGroup
                  value={alertResolution}
                  onChange={(event) => setAlertResolution(event.target.value as AlertResolution)}
                >
                  <FormControlLabel value="REAL_ALERT" control={<Radio />} label="Боевой сигнал" />
                  <FormControlLabel value="FALSE_ALARM" control={<Radio />} label="Ложный сигнал" />
                </RadioGroup>
              </FormControl>

              <TextField
                label="Комментарий"
                value={alertResolutionComment}
                onChange={(event) => setAlertResolutionComment(event.target.value)}
                multiline
                minRows={3}
                inputProps={{ maxLength: 1000 }}
                helperText={`${alertResolutionComment.length}/1000`}
                fullWidth
              />

              {similarAlertSignals.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={applyForSimilarAlerts}
                      onChange={(event) => setApplyForSimilarAlerts(event.target.checked)}
                      disabled={alertResolutionSaving}
                    />
                  }
                  label={`Применить для ${similarAlertSignals.length} аналогичных сигналов с ${similarAlertWindowLabel}`}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAlertDialog} disabled={alertResolutionSaving}>
            Отмена
          </Button>
          <Button
            onClick={handleResolveAlert}
            disabled={alertResolutionSaving || !selectedAlertSignal}
            variant="contained"
            color="error"
          >
            {alertResolutionSaving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manualLinkDialogOpen}
        onClose={handleCloseManualLinkDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          Связать сигнал с заявкой
          <IconButton
            aria-label="close"
            onClick={handleCloseManualLinkDialog}
            disabled={Boolean(manualLinkingSignalId)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {manualLinkSignal && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      ID сигнала
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      #{manualLinkSignal.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      terminal_number
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {manualLinkSignal.terminal_number || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Судно
                    </Typography>
                    <Typography variant="body1">{manualLinkSignal.vessel_name || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Тип
                    </Typography>
                    <Typography variant="body1">
                      {getSignalTypeLabel(manualLinkSignal.signal_type || 'UNKNOWN')}
                      {manualLinkSignal.original_alert_type
                        ? ` / ${manualLinkSignal.original_alert_type}`
                        : ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Статус
                    </Typography>
                    <Typography variant="body1">
                      {getSignalStatusLabel(manualLinkSignal.status)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Получен
                    </Typography>
                    <Typography variant="body1">
                      {formatUtcDate(manualLinkSignal.received_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Обновление координат
                    </Typography>
                    <Typography variant="body1">
                      {formatUtcDate(getCoordinateUpdateTime(manualLinkSignal).value)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Alert severity="info">Ручная привязка выполняется по решению ОД.</Alert>

              {!isTestSignal(manualLinkSignal) && (
                <Alert severity="error">
                  Внимание: тип сигнала не является тестовым. Ручная привязка к заявке выполняется
                  только по решению ОД и не заменяет обработку боевого сигнала.
                </Alert>
              )}

              {manualLinkDialogError && <Alert severity="error">{manualLinkDialogError}</Alert>}

              <Typography variant="subtitle1" fontWeight={700}>
                Заявки в статусе «ОЖИДАЕТ» по терминалу {manualLinkSignal.terminal_number || '—'}
              </Typography>

              {manualLinkCandidatesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : manualLinkCandidates.length === 0 ? (
                <Alert severity="info">
                  Заявки в статусе «ОЖИДАЕТ» по терминалу {manualLinkSignal.terminal_number || '—'}{' '}
                  не найдены.
                </Alert>
              ) : (
                <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID заявки</TableCell>
                        <TableCell>Судно</TableCell>
                        <TableCell>Терминал</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Создана</TableCell>
                        <TableCell>Интервал тестирования</TableCell>
                        <TableCell align="right">Действие</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {manualLinkCandidates.map((request) => (
                        <TableRow key={request.id} hover>
                          <TableCell>#{request.id}</TableCell>
                          <TableCell>{request.vessel_name || '—'}</TableCell>
                          <TableCell>
                            <code>{request.ssas_number || request.terminal_number || '—'}</code>
                          </TableCell>
                          <TableCell>
                            <Chip label="ОЖИДАЕТ" size="small" color="warning" variant="outlined" />
                          </TableCell>
                          <TableCell>{formatUtcDate(request.created_at)}</TableCell>
                          <TableCell>{formatRequestInterval(request)}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleConfirmManualLink(request)}
                              disabled={Boolean(manualLinkingSignalId)}
                            >
                              {manualLinkingSignalId === manualLinkSignal.id
                                ? 'Связь...'
                                : 'Связать'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManualLinkDialog} disabled={Boolean(manualLinkingSignalId)}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Модальное окно «Карточка заявки» ═══ */}
      <Dialog open={requestDialogOpen} onClose={handleCloseRequestDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          Карточка заявки #{selectedRequest?.request_number || selectedRequest?.id}
          <IconButton
            aria-label="close"
            onClick={handleCloseRequestDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {requestLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedRequest ? (
            <Box>
              {/* Статус */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1">Статус:</Typography>
                <Chip
                  label={getRequestStatusLabel(selectedRequest.status || '')}
                  color={getRequestStatusColor(selectedRequest.status || '')}
                  size="medium"
                />
              </Box>

              {/* Данные судна */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <DirectionsBoatIcon color="primary" />
                  Данные судна
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Название судна
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedRequest.vessel_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      IMO номер
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedRequest.imo_number || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      MMSI
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedRequest.mmsi || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Номер терминала ССТО
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedRequest.ssas_number || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Данные тестирования */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <ScheduleIcon color="primary" />
                  Данные тестирования
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Дата создания заявки
                    </Typography>
                    <Typography variant="body1">
                      {formatUtcDate(selectedRequest.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Плановая дата теста
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.planned_test_date
                        ? new Date(selectedRequest.planned_test_date).toLocaleString('ru-RU', {
                            timeZone: 'UTC',
                          }) + ' UTC'
                        : selectedRequest.test_date
                          ? new Date(selectedRequest.test_date).toLocaleString('ru-RU', {
                              timeZone: 'UTC',
                            }) + ' UTC'
                          : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Последнее обновление
                    </Typography>
                    <Typography variant="body1">
                      {formatUtcDate(selectedRequest.updated_at)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Контактная информация */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <PersonIcon color="primary" />
                  Контактная информация
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Организация
                        </Typography>
                        <Typography variant="body1">
                          {selectedRequest.owner_organization || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {selectedRequest.contact_email || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Телефон
                        </Typography>
                        <Typography variant="body1">
                          {selectedRequest.contact_phone || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Примечания */}
              {selectedRequest.notes && (
                <Paper sx={{ p: 2, backgroundColor: 'warning.light' }}>
                  <Typography variant="h6" gutterBottom>
                    Примечания
                  </Typography>
                  <Typography variant="body1">{selectedRequest.notes}</Typography>
                </Paper>
              )}

              {/* Системная информация */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                <Typography variant="caption" color="text.secondary">
                  ID записи: {selectedRequest.id}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequestDialog}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignalMonitor;
